import { z } from "zod";
import { addressSchema, blockTagSchema, chainReferenceSchema, evmChainSlugSchema, genericAddressSchema, hexDataSchema, hexQuantitySchema, isoTimestampSchema, nonCosmosChainSlugSchema, starknetChainSlugSchema, supportedChainSlugSchema, supportedChainTypeSchema, txHashSchema, } from "./common.js";
/** Use to request the latest block height for one chain. */
export const getBlockHeightParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
})
    .strict();
/** Use to request health info for one chain. */
export const getChainHealthParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
})
    .strict();
/** Use to look up one transaction receipt. */
export const getReceiptParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    hash: txHashSchema,
})
    .strict();
/** Use to request a wallet's native balance. */
export const getBalanceParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
/** Use to request a wallet's token balance. */
export const getTokenBalanceParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema,
    token: z.string().min(1),
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (nonCosmosChainSlugSchema.safeParse(value.chain).success &&
        !genericAddressSchema.safeParse(value.token).success) {
        context.addIssue({
            code: "custom",
            path: ["token"],
            message: "Expected a supported wallet address format",
        });
    }
});
/** Use for the compare-gas endpoint, which takes no params. */
export const compareGasParamsSchema = z.object({}).strict();
/** Use for one chain and block number in a batch block request. */
export const blockQueryItemSchema = z
    .object({
    chain: supportedChainSlugSchema,
    block: z.number().int().nonnegative(),
})
    .strict();
/** Use for one chain and tx hash in a batch transaction request. */
export const transactionHashQueryItemSchema = z
    .object({
    chain: supportedChainSlugSchema,
    hash: txHashSchema,
})
    .strict();
/** Use for one chain and block hash in a batch block request. */
export const blockHashQueryItemSchema = z
    .object({
    chain: supportedChainSlugSchema,
    hash: txHashSchema,
})
    .strict();
/** Use to request many blocks at once. Params is a positional array of items. */
export const getBlockByNumberParamsSchema = z
    .array(blockQueryItemSchema)
    .min(1)
    .max(50);
/** Use to request many transactions at once. Params is a positional array of items. */
export const getTransactionByHashParamsSchema = z
    .array(transactionHashQueryItemSchema)
    .min(1)
    .max(50);
/** Use to request many blocks by hash at once. Params is a positional array of items. */
export const getBlockByHashParamsSchema = z
    .array(blockHashQueryItemSchema)
    .min(1)
    .max(50);
/** Use to list the transactions inside one block. Supports EVM and Starknet chains. */
export const getBlockTransactionsParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    blockNumber: z.number().int().nonnegative(),
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
/** Use to request trace output for one transaction. EVM + Starknet (matches the chain dispatcher). */
export const traceTransactionParamsSchema = z
    .object({
    chain: z.union([evmChainSlugSchema, starknetChainSlugSchema]),
    hash: txHashSchema,
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
// Use to estimate gas / fees for one transaction.
export const estimateGasParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    to: genericAddressSchema,
    from: genericAddressSchema.optional(),
    value: z.string().min(1).optional(),
    data: hexDataSchema.optional(),
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
// Use to simulate one call without sending a transaction.
export const simulateCallParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    to: genericAddressSchema,
    data: hexDataSchema,
    from: genericAddressSchema.optional(),
    value: z.string().min(1).optional(),
    block: blockTagSchema.optional(),
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
/** Use to fetch bytecode for one contract address. */
export const getCodeParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
})
    .strict();
/** Use to simulate a call against the pending block. */
export const simulateCallPendingParamsSchema = z
    .object({
    chain: supportedChainSlugSchema,
    to: addressSchema,
    data: hexDataSchema,
    block: blockTagSchema,
})
    .strict();
/** Response shape for a block height lookup. */
export const blockHeightDataSchema = z
    .object({
    chain: chainReferenceSchema,
    height: z.number().int().nonnegative().optional(),
    error: z.string().min(1).optional(),
})
    .strict()
    .refine((value) => value.height !== undefined || value.error !== undefined, {
    message: 'Expected either "height" or "error"',
});
/** Response shape for a chain health check. */
export const chainHealthDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    status: z.enum(["ok", "degraded", "down"]),
    type: supportedChainTypeSchema,
    blockHeight: z.number().int().nonnegative().optional(),
    error: z.string().min(1).optional(),
    timestamp: isoTimestampSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === "ok" && value.blockHeight === undefined) {
        context.addIssue({
            code: "custom",
            message: "Healthy chain responses must include blockHeight",
            path: ["blockHeight"],
        });
    }
    if (value.status !== "ok" && value.error === undefined) {
        context.addIssue({
            code: "custom",
            message: "Non-ok chain responses must include error",
            path: ["error"],
        });
    }
});
/** Response shape for API service health. */
export const healthDataSchema = z
    .object({
    status: z.literal("ok"),
    timestamp: isoTimestampSchema,
})
    .strict();
/** Response shape for native or token balances. */
export const balanceDataSchema = z
    .object({
    chain: chainReferenceSchema,
    address: genericAddressSchema,
    token: z.string().min(1),
    /** Decimal-adjusted balance (human-readable), e.g. "1500.25" for 1500.25 USDC. */
    balance: z.string().min(1).optional(),
    /** On-chain integer (wei / lamports / satoshis / token base units), as a string. */
    balanceRaw: z.string().regex(/^\d+$/).optional(),
    /** Token decimals used to derive `balance` from `balanceRaw`. */
    decimals: z.number().int().min(0).max(36).optional(),
    error: z.string().min(1).optional(),
})
    .strict()
    .refine((value) => value.balance !== undefined || value.error !== undefined, {
    message: 'Expected either "balance" or "error"',
});
/** One row in the compare-gas response. */
// `compareGas` returns gas data for every EVM chain the runtime has configured,
// not just the public supported-chain slug set, so the chain field is left
// loose here (can be any non-empty identifier).
export const gasComparisonEntrySchema = z
    .object({
    chain: z.string().min(1),
    baseFeeGwei: z.number().nonnegative(),
    priorityFeeGwei: z.number().nonnegative(),
    estimatedTxCostUsd: z.number().nonnegative(),
})
    .strict();
/** Response shape for compare-gas results. */
export const gasComparisonDataSchema = z
    .object({
    results: z.array(gasComparisonEntrySchema),
    updatedAt: isoTimestampSchema,
})
    .strict();
/** One item in a batch block response. */
export const batchBlockItemDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
})
    .catchall(z.unknown());
/** Response shape for batch block lookups. */
export const batchBlockResultsDataSchema = z
    .object({
    count: z.number().int().nonnegative(),
    results: z.array(batchBlockItemDataSchema),
})
    .strict();
/** One item in a batch transaction response. */
export const batchTransactionItemDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    hash: txHashSchema.optional(),
})
    .catchall(z.unknown());
/** Response shape for batch transaction lookups. */
export const batchTransactionResultsDataSchema = z
    .object({
    count: z.number().int().nonnegative(),
    results: z.array(batchTransactionItemDataSchema),
})
    .strict();
/** One transaction inside a block transaction list. */
export const blockTransactionSchema = z
    .object({
    hash: txHashSchema,
    from: addressSchema,
    to: z.union([addressSchema, z.null()]),
    value: z.string().min(1),
    gas: z.string().min(1),
    gasPrice: z.string().min(1),
    input: hexDataSchema,
    nonce: z.string().min(1),
    blockNumber: z.string().min(1),
    blockHash: txHashSchema,
    transactionIndex: z.string().min(1),
})
    .catchall(z.unknown());
/** Response shape for block transaction lists. */
const evmBlockTransactionsDataSchema = z
    .object({
    blockNumber: z.number().int().nonnegative(),
    hash: txHashSchema,
    timestamp: z.number().int().nonnegative(),
    transactionCount: z.number().int().nonnegative(),
    transactions: z.array(blockTransactionSchema),
})
    .strict();
const starknetBlockTransactionsDataSchema = z
    .object({
    blockNumber: z.number().int().nonnegative(),
    hash: z.string(),
    timestamp: z.number().int().nonnegative(),
    transactionCount: z.number().int().nonnegative(),
    transactions: z.array(z.unknown()),
})
    .catchall(z.unknown());
export const blockTransactionsDataSchema = z.union([
    evmBlockTransactionsDataSchema,
    starknetBlockTransactionsDataSchema,
]);
/** Response shape for gas estimates. */
const evmGasEstimateDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    gasEstimate: z.number().int().nonnegative(),
    gasEstimateHex: hexQuantitySchema,
})
    .strict();
const starknetGasEstimateDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    request: z.unknown(),
    feeEstimate: z.unknown(),
})
    .catchall(z.unknown());
export const gasEstimateDataSchema = z.union([
    evmGasEstimateDataSchema,
    starknetGasEstimateDataSchema,
]);
/** Response shape for trace output. EVM trace includes `tracer`; Starknet's `starknet_traceTransaction` response is a single trace object with no tracer label. */
export const traceTransactionDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    hash: txHashSchema,
    tracer: z.string().min(1).optional(),
    trace: z.unknown(),
})
    .strict();
/** Response shape for simulated eth_call output. */
const evmSimulateCallDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    result: hexDataSchema,
})
    .strict();
const starknetSimulateCallDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    contractAddress: genericAddressSchema,
    entryPointSelector: hexDataSchema,
    calldata: z.array(z.string()),
    result: z.union([z.array(z.string()), z.null()]),
})
    .catchall(z.unknown());
export const simulateCallDataSchema = z.union([
    evmSimulateCallDataSchema,
    starknetSimulateCallDataSchema,
]);
/** Response shape for contract code lookups. */
const evmContractCodeDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    address: addressSchema,
    isContract: z.boolean(),
    bytecodeSize: z.number().int().nonnegative(),
    bytecode: hexDataSchema,
})
    .strict();
const starknetContractCodeDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema,
    contractClass: z.unknown(),
})
    .catchall(z.unknown());
export const contractCodeDataSchema = z.union([
    evmContractCodeDataSchema,
    starknetContractCodeDataSchema,
]);
/** One log entry inside a receipt response. */
export const evmReceiptLogSchema = z
    .object({
    address: addressSchema,
    topics: z.array(hexDataSchema).optional(),
    data: hexDataSchema,
    logIndex: z.string().min(1),
    blockHash: txHashSchema.optional(),
    blockNumber: z.string().min(1).optional(),
    blockTimestamp: z.string().min(1).optional(),
    transactionHash: txHashSchema.optional(),
    transactionIndex: z.string().min(1).optional(),
    transactionLogIndex: z.string().min(1).optional(),
    removed: z.boolean().optional(),
})
    .strict();
/** One entry in the handler-decorated `decodedLogs` array returned alongside the raw receipt. */
export const decodedReceiptLogSchema = z
    .object({
    address: z.string().min(1),
    topics: z.array(z.string().min(1)),
    data: z.string().min(1),
    logIndex: z.string().min(1),
    decoded: z
        .object({
        event: z.string().min(1),
    })
        .strict()
        .nullable(),
})
    .strict();
/**
 * Response shape for transaction receipts. L2s layer on chain-specific fields
 * (arbitrum: `gasUsedForL1`, `l1BlockNumber`, `timeboosted`; optimism-family:
 * `l1Fee`, `l1FeeScalar`, `l1GasPrice`, `l1GasUsed`), so this schema is loose
 * on top-level keys. The fixture exact-match step pins the actual payload.
 */
const evmReceiptDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    transactionHash: txHashSchema,
    transactionIndex: z.string().min(1),
    blockHash: txHashSchema,
    blockNumber: z.union([z.number().int().nonnegative(), z.string().min(1)]),
    from: addressSchema,
    to: z.union([addressSchema, z.null()]),
    cumulativeGasUsed: z.string().min(1),
    gasUsed: z.string().min(1),
    effectiveGasPrice: z.string().min(1).optional(),
    contractAddress: z.union([addressSchema, z.null()]).optional(),
    logs: z.array(evmReceiptLogSchema).optional(),
    logsBloom: z.string().min(1).optional(),
    status: z.union([z.string().min(1), z.number().int()]),
    type: z.string().min(1).optional(),
    decodedLogs: z.array(decodedReceiptLogSchema).optional(),
})
    .catchall(z.unknown());
const starknetReceiptDataSchema = z
    .object({
    chain: supportedChainSlugSchema,
    transaction_hash: txHashSchema,
    block_hash: txHashSchema,
    block_number: z.number().int().nonnegative(),
    execution_status: z.string().min(1),
    finality_status: z.string().min(1),
    type: z.string().min(1),
})
    .catchall(z.unknown());
export const receiptDataSchema = z.union([
    evmReceiptDataSchema,
    starknetReceiptDataSchema,
]);
